/**
 * @engine/input — InputControl Base
 *
 * Abstract base for all input controls (buttons, axes, vectors).
 * Controls hold state and are owned by InputDevice instances.
 */

import type { ControlType, ControlValue } from '../types';

/**
 * Base interface for all input controls.
 * Controls represent individual inputs on a device (button, axis, stick).
 */
export interface IInputControl<T extends ControlValue = ControlValue> {
  /** Control name (e.g., 'leftStick', 'space', 'leftButton') */
  readonly name: string;
  /** Control type for type checking */
  readonly type: ControlType;
  /** Current value */
  readonly value: T;
  /** Previous frame value */
  readonly previousValue: T;
  /** Has value changed this frame */
  readonly hasChanged: boolean;

  /**
   * Update control state (called by device)
   */
  update(newValue: T, time: number): void;

  /**
   * Reset to default state
   */
  reset(): void;

  /**
   * Read value with type casting
   */
  readValue<U extends T = T>(): U;
}

/**
 * Abstract base class for controls.
 * Provides common state tracking logic.
 */
export abstract class InputControl<T extends ControlValue = ControlValue>
  implements IInputControl<T>
{
  readonly name: string;
  abstract readonly type: ControlType;

  protected _value: T;
  protected _previousValue: T;
  protected _defaultValue: T;
  protected _lastUpdateTime: number = 0;

  constructor(name: string, defaultValue: T) {
    this.name = name;
    this._value = defaultValue;
    this._previousValue = defaultValue;
    this._defaultValue = defaultValue;
  }

  get value(): T {
    return this._value;
  }

  get previousValue(): T {
    return this._previousValue;
  }

  get hasChanged(): boolean {
    return !this.valuesEqual(this._value, this._previousValue);
  }

  update(newValue: T, time: number): void {
    this._previousValue = this._value;
    this._value = newValue;
    this._lastUpdateTime = time;
  }

  reset(): void {
    this._previousValue = this._value;
    this._value = this._defaultValue;
  }

  readValue<U extends T = T>(): U {
    return this._value as U;
  }

  /**
   * Compare two values for equality (override in subclasses for complex types)
   */
  protected valuesEqual(a: T, b: T): boolean {
    return a === b;
  }

  /**
   * Advance to next frame (previous = current)
   */
  commitFrame(): void {
    this._previousValue = this._value;
  }
}
